<template>
  <Dialog
    v-model:visible="isVisible"
    dismissable-mask
    :draggable="false"
    modal
    :header="campaign.id ? 'Edit campaign' : 'New campaign'"
    :style="{ width: '600px' }"
  >
    <div>
      <div :class="$style.formGroup">
        <label for="campaignName" :class="$style.label">Campaign name *</label>
        <InputText
          id="campaignName"
          v-model="campaignName"
          :class="{ [$style.pInvalid]: campaignName && isDuplicate }"
          placeholder="Name"
          autofocus
          @input="validate"
        />
        <small v-if="isDuplicate" :class="[$style.info, $style.pError]">Campaign name already exists</small>
        <small v-if="campaignName.length > 100" :class="[$style.info, $style.pError]">
          Campaign name should not exceed 100 characters
        </small>
        <small :class="[$style.info, !isCampaignNameValid && $style.pError]">
          Use only alphanumeric characters, dollar signs and spaces
        </small>
      </div>
    </div>

    <h2 :class="$style.title">Offers</h2>
    <div :class="$style.formGroup">
      <label for="offers" :class="$style.label">Select offers</label>
      <MultiSelect
        id="offers"
        v-model="selectedOffers"
        :options="unassignedOffers"
        option-label="name"
        placeholder="Select one or multiple offers"
        class="w-full md:w-14rem"
      />
    </div>

    <div v-if="flags.campaignPurchaseLimit.enabled">
      <h2 :class="$style.title">Behaviors</h2>
      <div :class="$style.formGroup">
        <label for="purchaseLimit" :class="$style.label">Purchase limit *</label>
        <div :class="$style.formRow">
          <InputNumber
            id="purchaseLimit"
            v-model="purchaseLimit"
            :class="$style.purchaseLimit"
            :disabled="isInfinite"
            :min="1"
            :max="999"
          />
          <Checkbox v-model="isInfinite" :class="$style.checkbox" binary input-id="isInfinite" name="isInfinite" />
          <label for="isInfinite">Infinite</label>
        </div>
      </div>
    </div>

    <p :class="[$style.required, $style.pValid]">* = required</p>
    <div :class="$style.actions" :style="{ justifyContent: campaign.id ? 'space-between' : 'flex-end' }">
      <Button v-if="campaign.id" label="Delete campaign" severity="danger" @click="showDeleteCampaignConfirm" />
      <div>
        <Button label="Cancel" class="p-button-primary" outlined style="margin-right: 0.5rem" @click="closeModal" />
        <Button
          label="Save"
          class="p-button-primary"
          :disabled="!campaignName || isDuplicate || !isCampaignNameValid"
          @click="save"
        />
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import Dialog from "primevue/dialog";
import InputNumber from "primevue/inputnumber";
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import { PropType, computed, ref, watch } from "vue";
import { useRoute } from "vue-router";

import useFeatureFlags from "@/hooks/useFeatureFlags";
import useCampaigns from "@/modules/Offers/hooks/useCampaigns";
import { useOffers } from "@/modules/Offers/hooks/useOffers";
import { Campaign, OfferItem } from "../types/types";
import { useConfirm } from "primevue/useconfirm";

const emit = defineEmits(["close"]);

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  campaign: {
    type: Object as PropType<Campaign>,
    default: () => ({
      id: "",
      name: "",
      purchaseLimit: 0,
      offers: [],
    }),
  },
});

const route = useRoute();
const confirm = useConfirm();

const gameId = ref(route.params.gameId as string);
const versionId = ref(route.params.versionId as string);

// const confirm = useConfirm();
const { flags } = useFeatureFlags(gameId.value);
const { campaigns, offersToDisplay, updateOffer, refetchOffers } = useOffers(gameId.value, versionId.value);
const { updateCampaign, createCampaign, deleteCampaign } = useCampaigns(gameId.value, versionId.value);

const unassignedOffers = computed(() =>
  offersToDisplay.value.filter((offer) => offer.campaignId === null && offer.status === "DRAFT")
);

const isVisible = computed({
  get: () => props.isVisible,
  set: (value) => {
    emit("close", value);
  },
});

const campaignName = ref("");
const isCampaignNameValid = ref(true);
const isDuplicate = ref(false);
const selectedOffers = ref<OfferItem[]>([]);
const purchaseLimit = ref(0);
const isInfinite = ref(false);

const resetCampaign = () => {
  campaignName.value = "";
  isCampaignNameValid.value = true;
  isDuplicate.value = false;
  selectedOffers.value = [];
  purchaseLimit.value = 1;
  isInfinite.value = false;
};

const validate = () => {
  campaignName.value = campaignName.value.trim();
  isCampaignNameValid.value = campaignName.value.length > 0 && campaignName.value.length <= 100;

  isDuplicate.value = campaigns.value.some(
    (campaign: Campaign) => campaign.name === campaignName.value && campaign.id !== props.campaign.id
  );
};

const handleOfferCampaignUpdate = async (offerId: string, campaignId: string) => {
  await updateOffer(offerId, { campaignId });
};

const closeModal = () => {
  resetCampaign();
  emit("close");
};

// Update offers sequentially in order to work with graphQL
const updateOffersSequentially = async (offers: OfferItem[], campaignId: string, index: number = 0) => {
  if (index < offers.length) {
    try {
      await handleOfferCampaignUpdate(offers[index].id, campaignId);
      await updateOffersSequentially(offers, campaignId, index + 1);
    } catch (error) {
      console.error("Error updating offer campaign:", error);
    }
  }
};

const save = async () => {
  validate();

  if (isCampaignNameValid.value && !isDuplicate.value) {
    const campaignData = {
      name: campaignName.value,
      purchaseLimit: purchaseLimit.value,
      id: props.campaign.id,
    };

    if (props.campaign.id) {
      await updateCampaign(campaignData);
      await updateOffersSequentially(selectedOffers.value, props.campaign.id);
    } else {
      const createdCampaign = await createCampaign(campaignData);
      if (createdCampaign?.id) {
        await updateOffersSequentially(selectedOffers.value, createdCampaign.id);
      }
    }

    await refetchOffers();
    closeModal();
  }
};

const showDeleteCampaignConfirm = () => {
  if (props.campaign.offers && props.campaign.offers.length > 0) {
    confirm.require({
      message: "You can only delete an empty campaign. To delete a campaign, first delete or move the offers inside.",
      header: "Delete campaign",
      rejectLabel: "Cancel",
      acceptClass: "hidden-button",
      rejectClass: "p-button-secondary p-button-outlined",
    });

    return;
  }
  confirm.require({
    message: "Are you sure? This process cannot be undone",
    header: "Delete campaign",
    rejectLabel: "Cancel",
    acceptLabel: "Delete",
    rejectClass: "p-button-secondary p-button-outlined",
    acceptClass: "p-button-danger",
    accept: async () => {
      await deleteCampaign(props.campaign.id);
    },
  });
};

watch(isInfinite, (newValue) => {
  purchaseLimit.value = newValue ? 0 : 1;
});

watch(
  isVisible,
  (newValue) => {
    if (newValue && props.campaign) {
      campaignName.value = props.campaign.name;
      purchaseLimit.value = props.campaign.purchaseLimit || 1;
      isInfinite.value = props.campaign.purchaseLimit === 0;
    }
  },
  { immediate: true }
);
</script>

<style module lang="scss">
.formGroup {
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.formRow {
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;

  .purchaseLimit {
    flex: 1;
  }
}

.label {
  margin-bottom: 0.5rem;
}

.title {
  margin-top: 2rem;
  margin-bottom: 1rem;
}

.pInvalid {
  border-color: var(--vd-color-error);
}

.actions {
  display: flex;
}

.pError {
  color: var(--vd-color-error);
  height: 8px;
}

.pValid {
  color: var(--vd-color-text-light-2);
}

.info {
  margin-top: 0.25rem;
}

.required {
  margin: 1.5rem 0;
  font-size: 12px;
}

.checkbox {
  margin-left: 0.5rem;
}
</style>
